<script lang="ts">
  import { appStore } from "$app/store.svelte"
  import { onMount } from "svelte"
  import Sortable from "sortablejs"
  import { xhr, toast, sortElementsForSortable, wait } from "$app/utils"
  import QuarterlyGoal from "./QuarterlyGoal.svelte"
  import { ChevronRight } from "lucide-svelte"
  import Honeybadger from "$app/utils/honeybadger"
  import { slide } from "svelte/transition"

  interface Props {
    department: DepartmentType
    expanded: boolean
  }

  let { department, expanded }: Props = $props()

  function updateGoals(newGoals: GoalType[]): void {
    newGoals.forEach((goal) => {
      const idx = appStore.goals.findIndex((g) => goal.id === g.id)
      appStore.goals[idx] = { ...appStore.goals[idx], ...goal }
    })
  }

  async function saveAssigneeGoals(
    newGoals: GoalType[],
    department_id: number
  ): Promise<void> {
    try {
      const resp = await xhr.post(
        `/plans/${department.plan_id}/goals/sort_by_department.json`,
        { goals: newGoals.map((g) => g.id), id: department_id }
      )
      updateGoals(resp.data as GoalType[])
    } catch (e) {
      const tempGoals = [...appStore.goals]
      appStore.goals = []
      await wait(30)
      appStore.goals = tempGoals
      toast("There was an error updating the sort order", "error")
      if (typeof Honeybadger.notify !== "undefined") {
        Honeybadger.notify(e)
      }
    }
  }

  let sortableSection: HTMLElement = $state()

  function setSortable(): void {
    if (!sortableSection) return
    const sort = Sortable.create(sortableSection, {
      animation: 200,
      group: "QuarterlyGoal",
      scrollSensitivity: 150,
      delay: 400,
      delayOnTouchOnly: true,
      direction: "vertical",
      ghostClass: "opacity-50",
      chosenClass: "border-green-400",
      dragClass: "border-green-400",
      filter: ".no-drag",
      emptyInsertThreshold: 20,
      onEnd: async (e: EventSortType) => {
        const sameContainer =
          e.from.dataset.department_id === e.to.dataset.department_id

        if (e.newIndex === e.oldIndex && sameContainer) return

        sort.options.sort = false // Disable sorting while saving

        if (sameContainer) {
          const department_id: number = parseInt(e.from.dataset.department_id)
          const currentGoals = appStore.goals
            .filter((g) => g.assignee_id === department_id)
            .sort((a, b) => a.assignee_position - b.assignee_position)
          const newGoals = sortElementsForSortable(
            currentGoals,
            e.newIndex,
            e.oldIndex
          ) as GoalType[]

          await saveAssigneeGoals(newGoals, department_id)
        } else {
          const department_id: number = parseInt(e.to.dataset.department_id)
          const currentGoals = appStore.goals
            .filter((g) => g.assignee_id === department_id)
            .sort((a, b) => a.assignee_position - b.assignee_position)
          const newGoal = appStore.goals.find(
            (g) => g.id === appStore.draggedElement.id
          )

          const newGoals = [
            ...currentGoals.slice(0, e.newIndex),
            newGoal,
            ...currentGoals.slice(e.newIndex)
          ]

          await saveAssigneeGoals(newGoals, department_id)
        }

        sort.options.sort = true
      },
      onStart: (evt: EventSortType) => {
        appStore.draggedElement = {
          ...appStore.draggedElement,
          type: "QuarterlyGoal",
          id: parseInt(evt.item.dataset.id),
          goal_id: 0
        }
      }
    })
  }

  let mounted = $state(false)
  let expandedDepartment = $state(true)

  $effect(() => {
    if (mounted && appStore.editable && expanded) {
      setTimeout(() => {
        setSortable()
      })
    }
    if (expanded) {
      expandedDepartment = true
    }
  })

  onMount(() => {
    mounted = true
  })
</script>

{#if mounted}
  {@const tGoals = appStore.goals.filter(
    (g) => g.assignee_id === department.id
  )}

  <!-- svelte-ignore a11y_click_events_have_key_events -->
  <div
    class="mt-6 mb-2 py-0.5 sm:py-2 flex justify-start"
    data-name="department-item"
    data-department_id={department.id}
    class:hover:cursor-pointer={tGoals.length > 0}
    role="button"
    tabindex="0"
    onclick={() => (expandedDepartment = !expandedDepartment)}
  >
    <div
      class={[
        "flex items-center pr-1 rotate-icon",
        expandedDepartment ? "open" : ""
      ]}
    >
      <ChevronRight class="text-gray-500 size-6 rotate-icon-90" />
    </div>
    <div class="text-2xl" data-name="department-name">
      {department.name}
    </div>
  </div>
  {#if expandedDepartment}
    {@const dGoals = appStore.goals
      .filter((g) => department.id === g.assignee_id)
      .sort((a, b) => a.assignee_position - b.assignee_position)}
    <div class="relative pl-1 sm:pl-8" transition:slide={{ duration: 200 }}>
      <section
        class="by-department"
        bind:this={sortableSection}
        data-department_id={department.id}
        style="min-height: 50px"
      >
        {#each dGoals as goal, idx (goal.id)}
          <div class="mb-2" data-type="QuarterlyGoal" data-id={goal.id}>
            <QuarterlyGoal bind:goal={dGoals[idx]} pos={idx + 1} {expanded} />
          </div>
        {:else}
          <div
            class="w-full border border-gray-100 py-2 px-6 h-14 flex items-center no-drag"
          >
            No Goals Found
          </div>
        {/each}
      </section>
    </div>
  {/if}
{/if}
